import type { PropsWithChildren } from 'react';
import { useId } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BrandS, BrandXS } from '@sorare/core/src/atoms/typography';
import { absCenter } from '@sorare/core/src/lib/style';

import { PodiumBar } from '../PodiumBar';

const Root = styled(Horizontal)`
  width: 100%;
`;
const Relative = styled.div`
  position: relative;
`;
const FullWidthRelative = styled(Relative)`
  flex: 1;
`;
const Wrapper = styled.div`
  ${absCenter}
  width: 100%;
  padding: var(--unit);
`;
const RankWrapper = styled(Horizontal).attrs({ center: true, gap: 0 })`
  ${absCenter}
`;

export enum State {
  BRONZE = 'BRONZE',
  SILVER = 'SILVER',
  GOLD = 'GOLD',
  SELECTED = 'SELECTED',
  MY_RANK = 'MY_RANK',
  BASIC = 'BASIC',
}

const getRankColorByState = (state: State) =>
  [State.MY_RANK, State.GOLD, State.SILVER, State.BRONZE].includesValue(state)
    ? 'var(--c-black)'
    : 'var(--c-white)';

export const getRankRowState = (
  isMyRanking: boolean,
  isFocused: boolean,
  rank: number | null
) => {
  if (isFocused) {
    return State.SELECTED;
  }
  if (rank === 1) {
    return State.GOLD;
  }
  if (rank === 2) {
    return State.SILVER;
  }
  if (rank === 3) {
    return State.BRONZE;
  }
  if (isMyRanking) {
    return State.MY_RANK;
  }
  return State.BASIC;
};

type Props = { state: State; rank: number | null };
export const RankRow = ({
  state,
  rank,
  children,
}: PropsWithChildren<Props>) => {
  const gradient1 = useId();
  const gradient2 = useId();
  const gradient3 = useId();
  const gradient4 = useId();
  const gradient5 = useId();
  const gradient6 = useId();
  const filter = useId();
  const mask1 = useId();
  const mask2 = useId();

  const gradients = {
    [State.BRONZE]: (
      <>
        <linearGradient
          id={gradient1}
          x1="109.311"
          y1="-15"
          x2="109.311"
          y2="-69"
          gradientUnits="userSpaceOnUse"
        >
          <stop />
          <stop offset="0.575292" stopOpacity="0.58" />
          <stop offset="1" stopOpacity="0" />
        </linearGradient>
        <linearGradient
          id={gradient2}
          x1="206.581"
          y1="-42"
          x2="272"
          y2="-42"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopOpacity="0" />
          <stop offset="1" stopColor="#FFAC70" />
        </linearGradient>
        <linearGradient
          id={gradient3}
          x1="228.516"
          y1="-42"
          x2="272"
          y2="-42"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FFAC70" stopOpacity="0" />
          <stop offset="1" stopColor="#FFAC70" />
        </linearGradient>
      </>
    ),
    [State.SILVER]: (
      <>
        <linearGradient
          id={gradient1}
          x1="109.311"
          y1="-15"
          x2="109.311"
          y2="-69"
          gradientUnits="userSpaceOnUse"
        >
          <stop />
          <stop offset="0.575292" stopOpacity="0.58" />
          <stop offset="1" stopOpacity="0" />
        </linearGradient>
        <linearGradient
          id={gradient2}
          x1="206.581"
          y1="-42"
          x2="272"
          y2="-42"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopOpacity="0" />
          <stop offset="1" stopColor="#99BDC8" />
        </linearGradient>
        <linearGradient
          id={gradient3}
          x1="228.516"
          y1="-42"
          x2="272"
          y2="-42"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#99BDC8" stopOpacity="0" />
          <stop offset="1" stopColor="#99BDC8" />
        </linearGradient>
      </>
    ),
    [State.GOLD]: (
      <>
        <linearGradient
          id={gradient1}
          x1="112.526"
          y1="-15"
          x2="112.526"
          y2="-69"
          gradientUnits="userSpaceOnUse"
        >
          <stop />
          <stop offset="0.575292" stopOpacity="0.58" />
          <stop offset="1" stopOpacity="0" />
        </linearGradient>
        <linearGradient
          id={gradient2}
          x1="212.657"
          y1="-42"
          x2="280"
          y2="-42"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopOpacity="0" />
          <stop offset="1" stopColor="#FCFF63" />
        </linearGradient>
        <linearGradient
          id={gradient3}
          x1="235.237"
          y1="-42"
          x2="280"
          y2="-42"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FCFF63" stopOpacity="0" />
          <stop offset="1" stopColor="#FCFF63" />
        </linearGradient>
      </>
    ),
    [State.MY_RANK]: (
      <>
        <linearGradient
          id={gradient1}
          x1="109.311"
          y1="-15"
          x2="109.311"
          y2="-69"
          gradientUnits="userSpaceOnUse"
        >
          <stop />
          <stop offset="0.575292" stopOpacity="0.58" />
          <stop offset="1" stopOpacity="0" />
        </linearGradient>
        <linearGradient
          id={gradient2}
          x1="206.581"
          y1="-290.4"
          x2="272"
          y2="-290.4"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopOpacity="0" />
          <stop offset="1" stopColor="white" />
        </linearGradient>
        <linearGradient
          id={gradient3}
          x1="228.516"
          y1="-290.4"
          x2="272"
          y2="-290.4"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" stopOpacity="0" />
          <stop offset="1" stopColor="white" />
        </linearGradient>
      </>
    ),
    [State.SELECTED]: null,
    [State.BASIC]: null,
  };

  const rankGradients = {
    [State.BRONZE]: (
      <>
        <linearGradient
          id={gradient4}
          x1="33.985"
          y1="58"
          x2="33.985"
          y2="4"
          gradientUnits="userSpaceOnUse"
        >
          <stop />
          <stop offset="0.575292" stopOpacity="0.58" />
          <stop offset="1" stopOpacity="0" />
        </linearGradient>
        <linearGradient
          id={gradient5}
          x1="65.234"
          y1="7.47525"
          x2="16.1129"
          y2="57.7526"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FFBF91" />
          <stop offset="0.2" stopColor="#FFA463" />
          <stop offset="0.887804" stopColor="#8E551E" />
        </linearGradient>
        <linearGradient
          id={gradient6}
          x1="4"
          y1="31"
          x2="78.6121"
          y2="31"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FFAC70" />
          <stop offset="1" stopColor="#7E4A14" stopOpacity="0.3" />
        </linearGradient>
      </>
    ),
    [State.SILVER]: (
      <>
        <linearGradient
          id={gradient4}
          x1="33.985"
          y1="58"
          x2="33.985"
          y2="4"
          gradientUnits="userSpaceOnUse"
        >
          <stop />
          <stop offset="0.575292" stopOpacity="0.58" />
          <stop offset="1" stopOpacity="0" />
        </linearGradient>
        <linearGradient
          id={gradient5}
          x1="67.8191"
          y1="6.59357"
          x2="4.6613"
          y2="58.8249"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
          <stop offset="0.456677" stopColor="#99BDC8" />
          <stop offset="1" stopColor="#616161" />
        </linearGradient>
        <linearGradient
          id={gradient6}
          x1="4"
          y1="31"
          x2="78.6121"
          y2="31"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#D8E5EA" />
          <stop offset="1" stopColor="#8D989A" stopOpacity="0.3" />
        </linearGradient>
      </>
    ),
    [State.GOLD]: (
      <>
        <linearGradient
          id={gradient4}
          x1="33.985"
          y1="58"
          x2="33.985"
          y2="4"
          gradientUnits="userSpaceOnUse"
        >
          <stop />
          <stop offset="0.575292" stopOpacity="0.58" />
          <stop offset="1" stopOpacity="0" />
        </linearGradient>
        <linearGradient
          id={gradient5}
          x1="78.7411"
          y1="5.38953"
          x2="37.7195"
          y2="78.4438"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
          <stop offset="0.352177" stopColor="#FCFF63" />
          <stop offset="1" stopColor="#C26900" />
        </linearGradient>
        <linearGradient
          id={gradient6}
          x1="2.95803"
          y1="31"
          x2="78.6122"
          y2="31"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FDFF99" />
          <stop offset="1" stopColor="#D08E19" stopOpacity="0.3" />
        </linearGradient>
      </>
    ),
    [State.MY_RANK]: (
      <>
        <linearGradient
          id={gradient4}
          x1="33.985"
          y1="58"
          x2="33.985"
          y2="4"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
        </linearGradient>
        <linearGradient
          id={gradient5}
          x1="78.7411"
          y1="5.38953"
          x2="37.7195"
          y2="78.4438"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
        </linearGradient>
        <linearGradient
          id={gradient6}
          x1="2.95803"
          y1="31"
          x2="78.6122"
          y2="31"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
        </linearGradient>
      </>
    ),
    [State.SELECTED]: (
      <>
        <linearGradient
          id={gradient4}
          x1="33.985"
          y1="58"
          x2="33.985"
          y2="4"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#7029FF" />
        </linearGradient>
        <linearGradient
          id={gradient5}
          x1="78.7411"
          y1="5.38953"
          x2="37.7195"
          y2="78.4438"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#7029FF" />
        </linearGradient>
        <linearGradient
          id={gradient6}
          x1="2.95803"
          y1="31"
          x2="78.6122"
          y2="31"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#7029FF" />
        </linearGradient>
      </>
    ),
    [State.BASIC]: null,
  };

  return (
    <Root gap={0}>
      <FullWidthRelative>
        <PodiumBar rank={rank} />
        <Wrapper>{children}</Wrapper>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="54"
          width="100%"
          style={state === State.SELECTED ? { stroke: '#7029FF' } : undefined}
          viewBox="0 0 272 54"
          fill="none"
          preserveAspectRatio="none"
        >
          <mask id={mask1} fill="white">
            <path d="M256.829 0C258.687 0 260.3 1.27873 260.724 3.08722L271.934 50.9166C271.978 51.1044 272 51.2966 272 51.4895C272 52.876 270.876 54 269.49 54H4.00013C1.79099 54 0.00012207 52.2091 0.00012207 50V4C0.00012207 1.79086 1.79098 0 4.00012 0H256.829Z" />
          </mask>
          {!!gradients[state] && (
            <path
              d="M256.829 0C258.687 0 260.3 1.27873 260.724 3.08722L271.934 50.9166C271.978 51.1044 272 51.2966 272 51.4895C272 52.876 270.876 54 269.49 54H4.00013C1.79099 54 0.00012207 52.2091 0.00012207 50V4C0.00012207 1.79086 1.79098 0 4.00012 0H256.829Z"
              fill={`url(#${gradient1})`}
            />
          )}
          {!!gradients[state] && (
            <path
              d="M256.829 0C258.687 0 260.3 1.27873 260.724 3.08722L271.934 50.9166C271.978 51.1044 272 51.2966 272 51.4895C272 52.876 270.876 54 269.49 54H4.00013C1.79099 54 0.00012207 52.2091 0.00012207 50V4C0.00012207 1.79086 1.79098 0 4.00012 0H256.829Z"
              fill={`url(#${gradient2})`}
              fillOpacity="0.3"
            />
          )}
          <path
            d="M271.934 50.9166L270.96 51.1448L271.934 50.9166ZM260.724 3.08722L259.75 3.31542L260.724 3.08722ZM260.724 3.08722L259.75 3.31542L270.96 51.1448L271.934 50.9166L272.908 50.6884L261.697 2.85903L260.724 3.08722ZM269.49 54V53H4.00013V54V55H269.49V54ZM0.00012207 50H1.00012V4H0.00012207H-0.999878V50H0.00012207ZM4.00012 0V1H256.829V0V-1H4.00012V0ZM0.00012207 4H1.00012C1.00012 2.34315 2.34327 1 4.00012 1V0V-1C1.2387 -1 -0.999878 1.23858 -0.999878 4H0.00012207ZM4.00013 54V53C2.34327 53 1.00012 51.6569 1.00012 50H0.00012207H-0.999878C-0.999878 52.7614 1.2387 55 4.00013 55V54ZM272 51.4895H271C271 52.3237 270.324 53 269.49 53V54V55C271.428 55 273 53.4283 273 51.4895H272ZM271.934 50.9166L270.96 51.1448C270.987 51.2578 271 51.3734 271 51.4895H272H273C273 51.2198 272.969 50.951 272.908 50.6884L271.934 50.9166ZM260.724 3.08722L261.697 2.85903C261.167 0.598405 259.151 -1 256.829 -1V0V1C258.222 1 259.432 1.95905 259.75 3.31542L260.724 3.08722Z"
            fill="white"
            fillOpacity="0.17"
            mask={`url(#${mask1})`}
          />
          {!!gradients[state] && (
            <path
              d="M271.934 50.9166L270.96 51.1448L271.934 50.9166ZM260.724 3.08722L259.75 3.31542L260.724 3.08722ZM260.724 3.08722L259.75 3.31542L270.96 51.1448L271.934 50.9166L272.908 50.6884L261.697 2.85903L260.724 3.08722ZM269.49 54V53H4.00013V54V55H269.49V54ZM0.00012207 50H1.00012V4H0.00012207H-0.999878V50H0.00012207ZM4.00012 0V1H256.829V0V-1H4.00012V0ZM0.00012207 4H1.00012C1.00012 2.34315 2.34327 1 4.00012 1V0V-1C1.2387 -1 -0.999878 1.23858 -0.999878 4H0.00012207ZM4.00013 54V53C2.34327 53 1.00012 51.6569 1.00012 50H0.00012207H-0.999878C-0.999878 52.7614 1.2387 55 4.00013 55V54ZM272 51.4895H271C271 52.3237 270.324 53 269.49 53V54V55C271.428 55 273 53.4283 273 51.4895H272ZM271.934 50.9166L270.96 51.1448C270.987 51.2578 271 51.3734 271 51.4895H272H273C273 51.2198 272.969 50.951 272.908 50.6884L271.934 50.9166ZM260.724 3.08722L261.697 2.85903C261.167 0.598405 259.151 -1 256.829 -1V0V1C258.222 1 259.432 1.95905 259.75 3.31542L260.724 3.08722Z"
              fill={`url(#${gradient3})`}
              mask={`url(#${mask1})`}
            />
          )}
          <defs>{gradients[state]}</defs>
        </svg>
      </FullWidthRelative>
      <Relative>
        <RankWrapper>
          <FormattedMessage
            id="RankRow.rank"
            defaultMessage="<big>{rank}</big><small>{rank, selectordinal, one {st} two {nd} few {rd} other {th}}</small>"
            values={{
              rank,
              big: content => (
                <BrandS color={getRankColorByState(state)}>{content}</BrandS>
              ),
              small: content => (
                <BrandXS
                  style={{ alignSelf: 'center' }}
                  color={getRankColorByState(state)}
                >
                  {content}
                </BrandXS>
              ),
            }}
          />
        </RankWrapper>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="60"
          width="auto"
          viewBox="0 0 83 62"
          fill="none"
          preserveAspectRatio="none"
        >
          <g filter={`url(#${filter})`}>
            <mask id={mask2} fill="white">
              <path d="M63.8291 4C65.6866 4 67.2997 5.27873 67.7236 7.08723L78.5048 53.0872C79.0931 55.5972 77.1883 58 74.6104 58H18.8281C16.9706 58 15.3575 56.7213 14.9337 54.9128L4.10078 8.69174C4.03382 8.40601 4 8.11352 4 7.82005C4 5.71029 5.71029 4 7.82005 4H63.8291Z" />
            </mask>
            <path
              d="M63.8291 4C65.6866 4 67.2997 5.27873 67.7236 7.08723L78.5048 53.0872C79.0931 55.5972 77.1883 58 74.6104 58H18.8281C16.9706 58 15.3575 56.7213 14.9337 54.9128L4.10078 8.69174C4.03382 8.40601 4 8.11352 4 7.82005C4 5.71029 5.71029 4 7.82005 4H63.8291Z"
              fill={`url(#${gradient4})`}
            />
            <path
              d="M63.8291 4C65.6866 4 67.2997 5.27873 67.7236 7.08723L78.5048 53.0872C79.0931 55.5972 77.1883 58 74.6104 58H18.8281C16.9706 58 15.3575 56.7213 14.9337 54.9128L4.10078 8.69174C4.03382 8.40601 4 8.11352 4 7.82005C4 5.71029 5.71029 4 7.82005 4H63.8291Z"
              fill={`url(#${gradient5})`}
            />
            <path
              d="M78.5048 53.0872L77.5312 53.3154L78.5048 53.0872ZM67.7236 7.08723L66.75 7.31543L67.7236 7.08723ZM67.7236 7.08723L66.75 7.31543L77.5312 53.3154L78.5048 53.0872L79.4784 52.859L68.6972 6.85904L67.7236 7.08723ZM74.6104 58V57H18.8281V58V59H74.6104V58ZM14.9337 54.9128L15.9073 54.6846L5.0744 8.46355L4.10078 8.69174L3.12717 8.91992L13.96 55.1409L14.9337 54.9128ZM7.82005 4V5H63.8291V4V3H7.82005V4ZM4 7.82005H5C5 6.26258 6.26258 5 7.82005 5V4V3C5.15801 3 3 5.15801 3 7.82005H4ZM4.10078 8.69174L5.0744 8.46355C5.02496 8.25262 5 8.03669 5 7.82005H4H3C3 8.19034 3.04267 8.5594 3.12717 8.91992L4.10078 8.69174ZM18.8281 58V57C17.435 57 16.2252 56.0409 15.9073 54.6846L14.9337 54.9128L13.96 55.1409C14.4899 57.4016 16.5062 59 18.8281 59V58ZM78.5048 53.0872L77.5312 53.3154C77.9724 55.1979 76.5438 57 74.6104 57V58V59C77.8328 59 80.2138 55.9965 79.4784 52.859L78.5048 53.0872ZM67.7236 7.08723L68.6972 6.85904C68.1674 4.59842 66.151 3 63.8291 3V4V5C65.2222 5 66.4321 5.95905 66.75 7.31543L67.7236 7.08723Z"
              fill="white"
              fillOpacity="0.17"
              mask={`url(#${mask2})`}
            />
            <path
              d="M78.5048 53.0872L77.5312 53.3154L78.5048 53.0872ZM67.7236 7.08723L66.75 7.31543L67.7236 7.08723ZM67.7236 7.08723L66.75 7.31543L77.5312 53.3154L78.5048 53.0872L79.4784 52.859L68.6972 6.85904L67.7236 7.08723ZM74.6104 58V57H18.8281V58V59H74.6104V58ZM14.9337 54.9128L15.9073 54.6846L5.0744 8.46355L4.10078 8.69174L3.12717 8.91992L13.96 55.1409L14.9337 54.9128ZM7.82005 4V5H63.8291V4V3H7.82005V4ZM4 7.82005H5C5 6.26258 6.26258 5 7.82005 5V4V3C5.15801 3 3 5.15801 3 7.82005H4ZM4.10078 8.69174L5.0744 8.46355C5.02496 8.25262 5 8.03669 5 7.82005H4H3C3 8.19034 3.04267 8.5594 3.12717 8.91992L4.10078 8.69174ZM18.8281 58V57C17.435 57 16.2252 56.0409 15.9073 54.6846L14.9337 54.9128L13.96 55.1409C14.4899 57.4016 16.5062 59 18.8281 59V58ZM78.5048 53.0872L77.5312 53.3154C77.9724 55.1979 76.5438 57 74.6104 57V58V59C77.8328 59 80.2138 55.9965 79.4784 52.859L78.5048 53.0872ZM67.7236 7.08723L68.6972 6.85904C68.1674 4.59842 66.151 3 63.8291 3V4V5C65.2222 5 66.4321 5.95905 66.75 7.31543L67.7236 7.08723Z"
              fill={`url(#${gradient6})`}
              mask={`url(#${mask2})`}
            />
          </g>
          <defs>
            {[
              State.BRONZE,
              State.SILVER,
              State.GOLD,
              State.MY_RANK,
            ].includesValue(state) && (
              <filter
                id={filter}
                x="0"
                y="0"
                width="82.6121"
                height="62"
                filterUnits="userSpaceOnUse"
                colorInterpolationFilters="sRGB"
              >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix
                  in="SourceAlpha"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                  result="hardAlpha"
                />
                <feOffset />
                <feGaussianBlur stdDeviation="2" />
                <feComposite in2="hardAlpha" operator="out" />
                {state === State.GOLD && (
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0.988235 0 0 0 0 1 0 0 0 0 0.388235 0 0 0 1 0"
                  />
                )}
                {state === State.SILVER && (
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0.847059 0 0 0 0 0.898039 0 0 0 0 0.917647 0 0 0 1 0"
                  />
                )}
                {state === State.BRONZE && (
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 1 0 0 0 0 0.67451 0 0 0 0 0.439216 0 0 0 1 0"
                  />
                )}
                <feBlend
                  mode="normal"
                  in2="BackgroundImageFix"
                  result="effect1_dropShadow_18390_161473"
                />
                <feBlend
                  mode="normal"
                  in="SourceGraphic"
                  in2="effect1_dropShadow_18390_161473"
                  result="shape"
                />
              </filter>
            )}
            {rankGradients[state]}
          </defs>
        </svg>
      </Relative>
    </Root>
  );
};
